// Clinic Day / Rapid Entry — focused single-record flow.
// Two independent columns side-by-side: (1) growth (weight/height) and
// (2) vaccinations. Each has its own reg-no lookup, fields, and reset
// state so staff can mix the two workflows back-to-back without one
// column's state interfering with the other.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    FiZap,
    FiAlertCircle,
    FiCheck,
    FiUser,
} from 'react-icons/fi';
import { profilesApi } from '../../api/endpoints/profiles';
import { growthApi } from '../../api/endpoints/growth';
import { vaccinationsApi } from '../../api/endpoints/vaccinations';
import { Avatar, useToast, StatusBadge, Skeleton } from '../../theme/components';
import { PageHeader } from '../../theme/components/PageHeader';
import { PageButton } from '../../theme/components/PageButton';
import { parseApiError } from '../../api/errors';
import { VACCINATION_PLACES } from '../../utils/constants';
import { todayISO } from '../../utils/dates';
import DateInput from '../Common/DateInput';
import './RapidEntry.css';

// Derive a human-readable age label from dob + date. Matches the existing
// age_at_record taxonomy in child_growth_records (varchar).
const computeAgeLabel = (dobStr, dateStr) => {
    if (!dobStr || !dateStr) return '';
    const dob = new Date(dobStr);
    const date = new Date(dateStr);
    if (Number.isNaN(dob.getTime()) || Number.isNaN(date.getTime())) return '';
    const months = (date.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * (365.25 / 12));
    if (months < 1) return 'Newborn';
    return `${Math.floor(months)} Months Completed`;
};

// Shared reg-no → child resolver. Used by both columns; the two columns
// each maintain their own `resolved` state because the workflows are
// independent (a staff member may resolve child A for growth then child
// B for vaccination in the same session).
const useChildResolver = (toast) => {
    const [regNo, setRegNo] = useState('');
    const [resolved, setResolved] = useState(null);
    const [resolving, setResolving] = useState(false);
    const [resolveError, setResolveError] = useState('');

    const lookup = useCallback(async () => {
        const reg = regNo.trim();
        if (!reg) {
            setResolveError('Please enter a registration number');
            return;
        }
        if (resolved && resolved.regNo === reg) return; // already resolved
        setResolving(true);
        setResolveError('');
        setResolved(null);
        try {
            const data = await profilesApi.getByRegNo(reg);
            setResolved({
                regNo: reg,
                id: data.id,
                name: data.name,
                gender: data.gender,
                dob: data.dob,
            });
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                setResolveError(`No child with reg no "${reg}"`);
            } else if (status === 410) {
                setResolveError('This profile is inactive');
            } else {
                setResolveError(parseApiError(e, 'Lookup failed.'));
            }
        } finally {
            setResolving(false);
        }
    }, [regNo, resolved, toast]);

    const reset = useCallback(() => {
        setRegNo('');
        setResolved(null);
        setResolveError('');
        setResolving(false);
    }, []);

    return { regNo, setRegNo, resolved, setResolved, resolving, resolveError, setResolveError, lookup, reset };
};

// --- Growth column ----------------------------------------------------

const GrowthColumn = ({ recordDate, onSaved, ageLabelOf }) => {
    const regNoRef = useRef(null);
    const weightRef = useRef(null);
    const heightRef = useRef(null);

    const resolver = useChildResolver();
    const { regNo, setRegNo, resolved, setResolved, resolving, resolveError, setResolveError, lookup, reset } = resolver;
    const ageLabel = resolved ? ageLabelOf(resolved.dob) : '';

    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        regNoRef.current?.focus();
    }, []);

    const validate = (activeResolved) => {
        if (!activeResolved) return 'Resolve a child first';
        if (!weight && !height) return 'Enter weight or height';
        if (weight) {
            const w = Number(weight);
            if (!Number.isFinite(w) || w < 0.5 || w > 50) return 'Weight must be 0.5–50 kg';
        }
        if (height) {
            const h = Number(height);
            if (!Number.isFinite(h) || h < 30 || h > 200) return 'Height must be 30–200 cm';
        }
        return null;
    };

    const save = async () => {
        let activeResolved = resolved;
        if (!activeResolved && regNo.trim()) {
            setSaving(true);
            setSaveError('');
            try {
                const reg = regNo.trim();
                const data = await profilesApi.getByRegNo(reg);
                activeResolved = {
                    regNo: reg,
                    id: data.id,
                    name: data.name,
                    gender: data.gender,
                    dob: data.dob,
                };
                setResolved(activeResolved);
            } catch (e) {
                const status = e?.response?.status;
                let errStr = '';
                if (status === 404) {
                    errStr = `No child with reg no "${regNo.trim()}"`;
                } else if (status === 410) {
                    errStr = 'This profile is inactive';
                } else {
                    errStr = parseApiError(e, 'Lookup failed.');
                }
                setSaveError(errStr);
                setSaving(false);
                return;
            }
        }

        const v = validate(activeResolved);
        if (v) {
            setSaveError(v);
            setSaving(false);
            return;
        }
        setSaving(true);
        setSaveError('');
        try {
            await growthApi.create(activeResolved.id, {
                date: recordDate,
                age: ageLabelOf(activeResolved.dob),
                weight: weight || '',
                height: height || '',
                remarks: '',
            });
            onSaved();
            setWeight('');
            setHeight('');
            setSaving(false);
            reset();
            setTimeout(() => regNoRef.current?.focus(), 30);
        } catch (e) {
            setSaveError(parseApiError(e, 'Save failed.'));
            setSaving(false);
        }
    };

    const handleRegNoKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            lookup().then(() => setTimeout(() => weightRef.current?.focus(), 80));
        } else if (e.key === 'Escape') {
            e.preventDefault();
            reset();
        }
    };

    const handleWeightKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            heightRef.current?.focus();
        }
    };

    const handleHeightKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            save();
        }
    };

    return (
        <section className="rapid-col" aria-label="Growth rapid entry">
            <h3 className="rapid-card-title">Growth</h3>

            <label className="rapid-step-label" htmlFor="re-growth-regno">Child Registration Number</label>

            <input
                ref={regNoRef}
                id="re-growth-regno"
                type="text"
                className={`rapid-input rapid-input-large ${resolveError ? 'has-error' : ''}`}
                value={regNo}
                onChange={(e) => {
                    setRegNo(e.target.value);
                    if (resolveError) setResolveError('');
                    if (resolved && resolved.regNo !== e.target.value.trim()) setResolved(null);
                }}
                onKeyDown={handleRegNoKeyDown}
                placeholder="e.g. 209"
                autoComplete="off"
                spellCheck={false}
                data-col="regNo-growth"
            />

            {resolving && (
                <div className="rapid-hint">
                    <Skeleton variant="text" width={180} height={20} />
                </div>
            )}
            {resolveError && (
                <div className="rapid-error">
                    <FiAlertCircle size={14} /> {resolveError}
                </div>
            )}
            {resolved && (
                <div className="rapid-resolved" aria-live="polite">
                    <Avatar
                        name={resolved.name}
                        id={resolved.id}
                        size={36}
                        sx={(theme) => ({
                            bgcolor:
                                resolved.gender?.toLowerCase() === 'female'
                                    ? '#ec4899'
                                    : theme.palette.primary.main,
                        })}
                    />
                    <div className="rapid-resolved-info">
                        <strong>{resolved.name}</strong>
                        <span>{resolved.gender} · {ageLabel}</span>
                    </div>
                    <StatusBadge severity="success">Resolved</StatusBadge>
                </div>
            )}

            <div className="rapid-divider" />

            <div className="rapid-row">
                <div className="rapid-field">
                    <label htmlFor="re-weight">Weight (kg)</label>
                    <input
                        id="re-weight"
                        ref={weightRef}
                        type="number"
                        step="0.01"
                        min="0.5"
                        max="50"
                        inputMode="decimal"
                        className="rapid-input"
                        value={weight}
                        onChange={(e) => {
                            setWeight(e.target.value);
                            if (saveError) setSaveError('');
                        }}
                        onKeyDown={handleWeightKeyDown}
                        placeholder="kg"
                        autoComplete="off"
                        data-col="weight"
                    />
                </div>

                <div className="rapid-field">
                    <label htmlFor="re-height">Height (cm)</label>
                    <input
                        id="re-height"
                        ref={heightRef}
                        type="number"
                        step="0.1"
                        min="30"
                        max="200"
                        inputMode="decimal"
                        className="rapid-input"
                        value={height}
                        onChange={(e) => {
                            setHeight(e.target.value);
                            if (saveError) setSaveError('');
                        }}
                        onKeyDown={handleHeightKeyDown}
                        placeholder="cm"
                        autoComplete="off"
                        data-col="height"
                    />
                </div>
            </div>

            {saveError && (
                <div className="rapid-error">
                    <FiAlertCircle size={14} /> {saveError}
                </div>
            )}

            <PageButton
                type="button"
                variant="primary"
                fullWidth
                onClick={save}
                disabled={saving}
            >
                {saving ? 'Saving…' : 'Press Enter to save'}
            </PageButton>

        </section>
    );
};

// --- Vaccination column -----------------------------------------------

const VaccinationColumn = ({ recordDate, onSaved, ageLabelOf }) => {
    const regNoRef = useRef(null);
    const vaccineRef = useRef(null);

    const resolver = useChildResolver();
    const { regNo, setRegNo, resolved, setResolved, resolving, resolveError, setResolveError, lookup, reset } = resolver;

    const [vaccines, setVaccines] = useState([]);
    const [vaccinesLoading, setVaccinesLoading] = useState(true);
    const [vaccinesError, setVaccinesError] = useState('');

    const [vaccineId, setVaccineId] = useState(''); // frontendId
    const [vaccineDate, setVaccineDate] = useState(recordDate);
    const [batchNo, setBatchNo] = useState('');
    const [place, setPlace] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [childVaccs, setChildVaccs] = useState([]);

    // True when the currently selected vaccine already has a Completed
    // record for this child. While true, the date / batch / place inputs
    // and the Save button stay disabled — the operator has to pick a
    // different vaccine (or change the reg no) to move forward. The
    // inline message below the select tells them why.
    const selectedVaccineRecord = vaccineId
        ? childVaccs.find((vc) => String(vc.id) === String(vaccineId))
        : null;
    const selectedIsCompleted = Boolean(
        selectedVaccineRecord && selectedVaccineRecord.status === 'Completed',
    );
    const selectedVaccineName = vaccineId
        ? (vaccines.find((v) => String(v.frontendId) === String(vaccineId))?.vaccineName || 'This vaccine')
        : '';

    // The user might have triggered a save attempt before childVaccs had
    // loaded — at that point selectedIsCompleted was false and validate()
    // set saveError to e.g. "Place of vaccination is required". Once the
    // history resolves and reveals the vaccine is already Completed, the
    // inline "X is already completed" message should be the only message
    // visible. Clear any stale saveError at that exact moment.
    useEffect(() => {
        if (selectedIsCompleted) setSaveError('');
    }, [selectedIsCompleted]);

    useEffect(() => {
        regNoRef.current?.focus();
    }, []);

    useEffect(() => {
        let cancelled = false;
        setVaccinesLoading(true);
        vaccinationsApi
            .catalogue()
            .then((rows) => {
                if (cancelled) return;
                setVaccines(rows || []);
            })
            .catch((e) => {
                if (cancelled) return;
                setVaccinesError(parseApiError(e, 'Could not load vaccine list.'));
            })
            .finally(() => {
                if (!cancelled) setVaccinesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!resolved) {
            setChildVaccs([]);
            return;
        }
        let cancelled = false;
        vaccinationsApi
            .list(resolved.id)
            .then((history) => {
                if (cancelled) return;
                setChildVaccs(history || []);
            })
            .catch((e) => {
                console.error('Failed to load child vaccination history:', e);
            });
        return () => {
            cancelled = true;
        };
    }, [resolved]);

    const validate = (activeResolved) => {
        if (!activeResolved) return 'Resolve a child first';
        if (!vaccineId) return 'Select a vaccine';
        if (!vaccineDate) return 'Date is required';
        if (!place) return 'Place of vaccination is required';
        if (!batchNo.trim()) return 'Batch number is required';
        return null;
    };

    const save = async () => {
        let activeResolved = resolved;
        if (!activeResolved && regNo.trim()) {
            setSaving(true);
            setSaveError('');
            try {
                const reg = regNo.trim();
                const data = await profilesApi.getByRegNo(reg);
                activeResolved = {
                    regNo: reg,
                    id: data.id,
                    name: data.name,
                    gender: data.gender,
                    dob: data.dob,
                };
                setResolved(activeResolved);
            } catch (e) {
                const status = e?.response?.status;
                let errStr = '';
                if (status === 404) {
                    errStr = `No child with reg no "${regNo.trim()}"`;
                } else if (status === 410) {
                    errStr = 'This profile is inactive';
                } else {
                    errStr = parseApiError(e, 'Lookup failed.');
                }
                setSaveError(errStr);
                setSaving(false);
                return;
            }
        }

        const v = validate(activeResolved);
        if (v) {
            setSaveError(v);
            setSaving(false);
            return;
        }

        setSaving(true);
        setSaveError('');

        // Verify if vaccine is already completed
        let activeVaccs = childVaccs;
        if (!resolved && activeResolved) {
            try {
                activeVaccs = await vaccinationsApi.list(activeResolved.id);
                setChildVaccs(activeVaccs);
            } catch (err) {
                activeVaccs = [];
            }
        }

        const existingRecord = activeVaccs.find((vc) => String(vc.id) === String(vaccineId));
        if (existingRecord && existingRecord.status === 'Completed') {
            const vaccineObj = vaccines.find((vac) => String(vac.frontendId) === String(vaccineId));
            const vaccineName = vaccineObj ? vaccineObj.vaccineName : 'This vaccine';
            setSaveError(`${vaccineName} is already completed.`);
            setSaving(false);
            return;
        }

        try {
            await vaccinationsApi.quickUpdate(activeResolved.id, vaccineId, {
                date: vaccineDate,
                batchNo: batchNo.trim(),
                place: place,
            });
            // Notify the rest of the app (e.g. Vaccination tab) so they
            // can refetch in-place.
            window.dispatchEvent(
                new CustomEvent('phm:vaccination-updated', { detail: { childId: activeResolved.id } }),
            );
            onSaved();
            setVaccineId('');
            setVaccineDate(recordDate);
            setBatchNo('');
            setPlace('');
            setSaving(false);
            reset();
            setTimeout(() => regNoRef.current?.focus(), 30);
        } catch (e) {
            setSaveError(parseApiError(e, 'Save failed.'));
            setSaving(false);
        }
    };

    const handleRegNoKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            lookup().then(() => {
                if (vaccineRef.current) setTimeout(() => vaccineRef.current?.focus(), 80);
            });
        } else if (e.key === 'Escape') {
            e.preventDefault();
            reset();
        }
    };

    const handleBatchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            save();
        }
    };

    return (
        <section className="rapid-col" aria-label="Vaccination rapid entry">
            <h3 className="rapid-card-title">Vaccinations</h3>

            <label className="rapid-step-label" htmlFor="re-vacc-regno">Child Registration Number</label>

            <input
                ref={regNoRef}
                id="re-vacc-regno"
                type="text"
                className={`rapid-input rapid-input-large ${resolveError ? 'has-error' : ''}`}
                value={regNo}
                onChange={(e) => {
                    setRegNo(e.target.value);
                    if (resolveError) setResolveError('');
                    if (resolved && resolved.regNo !== e.target.value.trim()) setResolved(null);
                }}
                onKeyDown={handleRegNoKeyDown}
                placeholder="e.g. 209"
                autoComplete="off"
                spellCheck={false}
                data-col="regNo-vacc"
            />

            {resolving && (
                <div className="rapid-hint">
                    <Skeleton variant="text" width={180} height={20} />
                </div>
            )}
            {resolveError && (
                <div className="rapid-error">
                    <FiAlertCircle size={14} /> {resolveError}
                </div>
            )}
            {resolved && (
                <div className="rapid-resolved" aria-live="polite">
                    <Avatar
                        name={resolved.name}
                        id={resolved.id}
                        size={36}
                        sx={(theme) => ({
                            bgcolor:
                                resolved.gender?.toLowerCase() === 'female'
                                    ? '#ec4899'
                                    : theme.palette.primary.main,
                        })}
                    />
                    <div className="rapid-resolved-info">
                        <strong>{resolved.name}</strong>
                        <span>
                            <FiUser size={11} /> {resolved.gender} · {ageLabelOf(resolved.dob)}
                        </span>
                    </div>
                    <StatusBadge severity="success">Resolved</StatusBadge>
                </div>
            )}

            <div className="rapid-divider" />

            <div className="rapid-field">
                <label htmlFor="re-vaccine">Vaccine</label>
                <select
                    id="re-vaccine"
                    ref={vaccineRef}
                    className="rapid-input"
                    value={vaccineId}
                    onChange={(e) => {
                        const val = e.target.value;
                        setVaccineId(val);
                        if (saveError) setSaveError('');
                    }}
                    disabled={vaccinesLoading}
                    data-col="vaccine"
                >
                    <option value="">
                        {vaccinesLoading ? 'Loading vaccines…' : 'Select vaccine…'}
                    </option>
                    {vaccines.map((v) => (
                        <option key={v.frontendId} value={v.frontendId}>
                            {v.vaccineName}
                        </option>
                    ))}
                </select>
                {selectedIsCompleted && (
                    <div className="rapid-error">
                        <FiAlertCircle size={14} /> {selectedVaccineName} is already completed.
                    </div>
                )}
                {vaccinesError && (
                    <div className="rapid-error">
                        <FiAlertCircle size={14} /> {vaccinesError}
                    </div>
                )}
            </div>

            <div className="rapid-row">
                <div className="rapid-field">
                    <label htmlFor="re-vdate">Date Given</label>
                    <DateInput
                        id="re-vdate"
                        className="rapid-input"
                        value={vaccineDate}
                        max={recordDate}
                        disabled={selectedIsCompleted}
                        onChange={(e) => {
                            setVaccineDate(e.target.value);
                            if (saveError) setSaveError('');
                        }}
                        data-col="vaccine-date"
                    />
                </div>
                <div className="rapid-field">
                    <label htmlFor="re-vbatch">Batch No</label>
                    <input
                        id="re-vbatch"
                        type="text"
                        className="rapid-input"
                        value={batchNo}
                        disabled={selectedIsCompleted}
                        onChange={(e) => {
                            setBatchNo(e.target.value);
                            if (saveError) setSaveError('');
                        }}
                        onKeyDown={handleBatchKeyDown}
                        placeholder="Batch number"
                        autoComplete="off"
                        data-col="vaccine-batch"
                    />
                </div>
            </div>

            <div className="rapid-field">
                <label htmlFor="re-place">Place of Vaccination</label>
                <select
                    id="re-place"
                    className="rapid-input"
                    value={place}
                    disabled={selectedIsCompleted}
                    onChange={(e) => {
                        setPlace(e.target.value);
                        if (saveError) setSaveError('');
                    }}
                    data-col="vaccine-place"
                >
                    <option value="">Select place…</option>
                    {VACCINATION_PLACES.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>
            </div>

            {saveError && !selectedIsCompleted && (
                <div className="rapid-error">
                    <FiAlertCircle size={14} /> {saveError}
                </div>
            )}

            <PageButton
                type="button"
                variant="primary"
                fullWidth
                onClick={save}
                disabled={saving || selectedIsCompleted}
            >
                {saving ? 'Saving…' : 'Press Enter to record'}
            </PageButton>

        </section>
    );
};

// --- Page -------------------------------------------------------------

const RapidEntry = () => {
    const toast = useToast();

    const [recordDate] = useState(todayISO());
    const ageLabelOf = useCallback(
        (dob) => computeAgeLabel(dob, recordDate),
        [recordDate],
    );

    // Shared session counter. Both columns bump this on success.
    const [sessionCount, setSessionCount] = useState(0);
    const handleSaved = useCallback(
        (label) => {
            setSessionCount((c) => c + 1);
            toast.success(label, { duration: 1400 });
        },
        [toast],
    );

    return (
        <div className="rapid-page">

            <PageHeader
                title="Clinic Day Rapid Entry"
                subtitle="Record growth or vaccinations — type Reg No → fill the form → press Enter to save."
                icon={FiZap}
            />

            {sessionCount > 0 && (
                <div className="rapid-session-pill">
                    <StatusBadge severity="success" title="Records saved this session">
                        <FiCheck size={12} /> {sessionCount} saved this session
                    </StatusBadge>
                </div>
            )}

            <main className="rapid-main">
                <div className="rapid-card-split">
                    <div className="rapid-card">
                        <GrowthColumn
                            recordDate={recordDate}
                            ageLabelOf={ageLabelOf}
                            onSaved={() => handleSaved('Saved — ready for next child.')}
                        />
                    </div>
                    <div className="rapid-card">
                        <VaccinationColumn
                            recordDate={recordDate}
                            ageLabelOf={ageLabelOf}
                            onSaved={() => handleSaved('Vaccination recorded — ready for next child.')}
                        />
                    </div>
                </div>
            </main>
        </div>
    );
};

export default RapidEntry;
